import type { CommunityEntry } from "@cfg/api-contract";
import { useId, useMemo, useState } from "react";
import { useApp } from "../app-context.tsx";
import { bestEntryForSet, communitySets, matchSet } from "../community.ts";

export interface StartFromSetProps {
  designId: string;
  /** Community entries of this design and its family. */
  items: CommunityEntry[] | undefined;
  /** Put the chosen entry's dimensions on the model as the user's own. */
  onStart: (entry: CommunityEntry, ref: string) => void | Promise<void>;
}

/**
 * "Start from a set" above the dimensions: pick a set the community tagged for this
 * bracket (or its family) and start from the best-printed dimensions for it, ready to
 * check and adjust. Hidden when nothing is tagged yet.
 */
export function StartFromSet({ designId, items, onStart }: StartFromSetProps) {
  const { brand, copy } = useApp();
  const id = useId();
  const sets = useMemo(() => communitySets(items ?? []), [items]);
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  if (!sets.length) return null;
  const label = brand.config.fitTags.label;
  const submit = () => {
    const set = matchSet(sets, value);
    const entry = set && bestEntryForSet(items ?? [], set.ref, designId);
    if (!set || !entry) return setError(copy("configurator.startFromSetNone", { ref: value.trim() }));
    setError(null);
    setValue("");
    void onStart(entry, set.ref);
  };
  return (
    <form
      className="start-from-set"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <label className="start-from-set-label" htmlFor={id}>
        {copy("configurator.startFromSet", { label })}
      </label>
      <div className="start-from-set-row">
        <input
          id={id}
          className="cfg-input start-from-set-input"
          list={`${id}-sets`}
          value={value}
          placeholder={brand.config.fitTags.refPlaceholder ?? sets[0]!.ref}
          autoComplete="off"
          aria-describedby={`${id}-hint${error ? ` ${id}-error` : ""}`}
          aria-invalid={!!error || undefined}
          onChange={(e) => {
            setValue(e.target.value);
            setError(null);
          }}
        />
        <datalist id={`${id}-sets`}>
          {sets.map((s) => (
            <option key={s.ref} value={s.ref} label={[s.name, s.prints > 0 ? `✓ ${s.prints}` : ""].filter(Boolean).join(" · ") || undefined} />
          ))}
        </datalist>
        <button type="submit" className="cfg-btn start-from-set-use" disabled={!value.trim()}>
          {copy("configurator.startFromSetUse")}
        </button>
      </div>
      <p className="muted small start-from-set-hint" id={`${id}-hint`}>
        {copy("configurator.startFromSetHint")}
      </p>
      {error && (
        <p className="cfg-error start-from-set-error" id={`${id}-error`} role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
